<script setup lang="ts">
import { InputField, InputSwitch, InputTextarea } from '@caks/uikit/input'
import { DropdownMain } from '@caks/uikit/dropdown'
import { getUserAddresses, getProfileData, getPickupPoints } from '@caks/shared/api/user.route'
import { useAccountStore } from '@caks/shared/store/store.account'
import { vMaska } from 'maska'
import { PickupPoint } from '@caks/shared/types/types.api'

interface Emit {
  (
    emit: 'update-address-info',
    value: {
      name: string
      phone: string
      address: string
      time: string
    }
  ): void
}

const props = defineProps<{ orderType: 'delivery' | 'pickup' }>()
const store = useAccountStore()
const emit = defineEmits<Emit>()

const timeTitles = {
  delivery: 'Время доставки',
  pickup: 'Ко скольки приготовить?',
}

const addressTitles = {
  delivery: 'Адрес доставки',
  pickup: 'Адрес самовывоза',
}

const addresses = ref<{ id: number; name: string }[]>([])

function fillTimeArray() {
  const timeArray = []
  const currentTime = new Date()
  const currentHour = currentTime.getHours()
  const currentMinute = currentTime.getMinutes()
  const startHour = currentHour
  let startMinute = currentMinute < 30 ? 30 : 0

  for (let i = startHour; i < 22; i++) {
    for (let j = startMinute; j < 60; j += 30) {
      const timeSlot =
        i + ':' + (j === 0 ? '00' : '30') + '-' + (i + 1) + ':' + (j === 0 ? '30' : '00')
      timeArray.push(timeSlot)
    }
    startMinute = 0
  }

  return timeArray.map((el, index) => {
    return {
      id: index + 1,
      name: el,
    }
  })
}

const filledtime = fillTimeArray()
const timeMin = ref<{ hours: number; minutes: number } | null>(null)

const orderInfo = reactive<any>({
  name: {
    title: 'Имя',
    value: '',
    component: shallowRef(InputField),
    require: true,
    props: {
      placeholder: 'Ваше имя',
    },
  },
  phone: {
    title: 'Телефон',
    value: '',
    component: shallowRef(InputField),
    require: true,
    type: 'phone',
    props: {
      placeholder: 'Ваш номер телефона',
    },
  },
  address: {
    title: addressTitles[props.orderType],
    value: null,
    component: shallowRef(DropdownMain),
    require: true,
    props: {
      placeholder: 'Адрес',
      options: addresses,
    },
  },
  time: {
    title: timeTitles[props.orderType],
    value: null,
    component: shallowRef(DropdownMain),
    require: props.orderType !== 'delivery',
    type: 'time',
    props: {
      placeholder: 'Время',
    },
  },
  comment: {
    title: 'Комментарий',
    value: '',
    component: shallowRef(InputTextarea),
    require: false,
    type: 'text',
    props: {
      placeholder: 'Ваш комментарий здесь...',
    },
  },
})

watch(orderInfo, (newValue) => {
  const { name, phone, address, time } = newValue

  const info = {
    name: name.value,
    phone: phone.value.replaceAll('+', '').replaceAll('-', ''),
    //@ts-ignore
    address: address.value?.id,
    //@ts-ignore
    time: `${time.value.hours}:${time.value.minutes}`,
  }

  //@ts-ignore
  emit('update-address-info', info)
})

watch(
  () => orderInfo.address.value,
  () => {
    const address = {
      id: orderInfo.address.value.id,
      title: orderInfo.address.value.title,
      iiko_id: orderInfo.address.value.iiko_id,
    }

    store.setLocation(address)
    localStorage.setItem('location', JSON.stringify(address))
  }
)

watch(
  () => orderInfo.time.value,
  () => {
    store.setDesiredTime(`${orderInfo.time.value.hours}:${orderInfo.time.value.minutes}`)
  }
)

watch(
  () => orderInfo.comment.value,
  () => {
    store.setCartComment(orderInfo.comment.value)
  }
)

onMounted(async () => {
  try {
    if (store.orderType === 'delivery') {
      const { data: userAddresses } = await getUserAddresses()

      addresses.value = userAddresses.map((el: any) => {
        return {
          id: el.id,
          name: `${el.street}, ${el.house_number}, кв. ${el.apartment}`,
        }
      })

      const selectedAddress = addresses.value.find((el) => el.id === store.selectedLocation?.id)

      if (selectedAddress) {
        orderInfo.address.value = selectedAddress
      }

      orderInfo.time.value = 'Побыстрее'
    } else {
      const cityId = localStorage.getItem('caksCityId') ?? '0'
      const {
        data: { pickup_points: pickupAddresses },
      } = await getPickupPoints(+cityId)
      addresses.value = (pickupAddresses as PickupPoint[]).map((el) => {
        return {
          id: el.id,
          name: el.name,
          iiko_id: el.iiko_id,
          title: el.title,
        }
      })

      const selectedAddress = addresses.value.find((el) => el.id === store.selectedLocation?.id)

      if (selectedAddress) {
        orderInfo.address.value = selectedAddress
      }

      const minBookingTime = new Date(new Date().setMinutes(new Date().getMinutes() + 15))
      const hours = minBookingTime.getHours()
      const minutes = minBookingTime.getMinutes()

      const time = `${hours}:${minutes < 10 ? '0' + minutes : minutes}`
      orderInfo.time.value = { hours, minutes }
      timeMin.value = { hours, minutes }

      orderInfo.orderMethod = {
        title: 'Способ получения заказа',
        value: 0,
        component: InputSwitch,
        props: {
          options: ['Заберу с собой', 'Поем у вас'],
        },
      }
    }

    orderInfo.comment.value = store.cartComment

    const { data: user } = await getProfileData()
    orderInfo.name.value = user.name as string
    orderInfo.phone.value = user.phone as string
  } catch (error) {
    console.error(error)
  }
})
</script>

<template>
  <div class="address-info">
    <div
      v-for="item in orderInfo"
      class="address-info__field"
      :class="{ 'address-info__field--require': item.require }"
    >
      <p>{{ item.title }}</p>
      <component
        v-if="item.type !== 'phone' && item.type !== 'time' && item.props.placeholder !== 'Адрес'"
        v-model="item.value"
        v-bind="item.props"
        :is="item.component"
      />
      <input-field
        v-else-if="item.type === 'phone'"
        v-maska
        data-maska="+7-###-###-##-##"
        v-model="item.value"
        inputmode="numeric"
        no-icons
        placeholder="Номер телефона"
      />
      <div v-else-if="item.props.placeholder === 'Адрес'" class="address-info__address">
        {{ orderInfo?.address?.value?.name }}
      </div>
      <div v-else>
        <div v-if="props.orderType === 'delivery'" class="address-info__time">Побыстрее</div>
        <VueDatePicker
          v-else
          v-model="item.value"
          :min-time="timeMin"
          :locale="'ru'"
          time-picker
        ></VueDatePicker>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.address-info {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__field {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    max-width: 640px;

    position: relative;

    &--require {
      &:after {
        content: '*';
        position: absolute;
        top: 5px;
        right: 5px;
        @include h5-2;
      }
    }

    p {
      max-width: 216px;
      width: 50%;
      @include h5-2;
    }

    div {
      width: 100%;
    }

    &:last-child {
      &:after {
        content: none;
      }
    }
  }

  &__time,
  &__address {
    padding: 0.5rem 1rem;
    height: 2.75rem;
    display: flex;
    align-items: center;
    @include h5-2;
  }
}
</style>
